import { useSwitch } from "@mui/base/useSwitch";
import {
  useFirebaseRegistrationToken,
  useNotificationUtils,
  useSubscriptionsList,
} from "@product/scmp-sdk";
import { useCallback, useMemo, useState } from "react";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { Variant as LoadingVariant } from "~/components/common/loading/enums";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { sendMirrorCustomEvent } from "~/components/tracking/mirror";
import { getFirebaseApp } from "~/lib/firebase";
import { firebaseTopic } from "~/lib/hooks";
import type { liveUpdateSwitchLiveArticle$key } from "~/queries/__generated__/liveUpdateSwitchLiveArticle.graphql";

import {
  Container,
  StyledLoading,
  SwitchInput,
  SwitchRoot,
  SwitchThumb,
  SwitchTrack,
  Text,
} from "./styles";

type Props = {
  className?: string;
  entityId: string;
  reference: liveUpdateSwitchLiveArticle$key;
};

export const LiveArticleLiveUpdateSwitch: FunctionComponent<Props> = ({
  className,
  entityId,
  reference,
}) => {
  const { refreshRegistrationToken, registrationToken } = useFirebaseRegistrationToken(
    getFirebaseApp(),
  );
  const { isDenied, isGranted, requestPermission } = useNotificationUtils();
  const { setSubscriptionList, subscriptionList = [] } = useSubscriptionsList();

  const liveArticle = useFragment(
    graphql`
      fragment liveUpdateSwitchLiveArticle on LiveArticle {
        status
        pushNotifications
      }
    `,
    reference,
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const topic = `live-${entityId}`;
  const isEnabled = useMemo(
    () => !isLoading && liveArticle?.status === true && liveArticle?.pushNotifications === true,
    [isLoading, liveArticle?.pushNotifications, liveArticle?.status],
  );

  const handleSwitchChange = useCallback(
    async (isChecked: boolean) => {
      if (typeof window === "undefined") return;
      if (registrationToken?.status === "error") return;
      if (isDenied) return;

      const payload = {
        token: registrationToken?.result ?? "",
        topic,
      };

      if (!isGranted) {
        const result = await requestPermission();
        if (result !== "granted") return;

        sendGATracking({
          action: "Push Notification/Allow",
          category: "Push Notification PWA",
          label: topic,
        });

        try {
          const refreshedToken = await refreshRegistrationToken();
          payload.token = refreshedToken ?? registrationToken?.result ?? "";
        } catch (error) {
          console.error(error);
          return;
        }
      }

      if (isChecked) {
        sendMirrorCustomEvent({
          event_name: "scmp_alerts",
          topic,
        });
      }
      await (isChecked ? firebaseTopic.subscribe(payload) : firebaseTopic.unsubscribe(payload));

      setSubscriptionList(current => {
        const topics = current ?? [];
        return isChecked ? [...topics, topic] : topics.filter(item => item !== topic);
      });

      sendGATracking({
        action: `Live Blog Push PWA Notification/Switch ${isChecked ? "on" : "off"}` as const,
        category: "Live Blog Push PWA",
        label: window.location.href,
      });
    },
    [
      isDenied,
      isGranted,
      refreshRegistrationToken,
      registrationToken?.result,
      registrationToken?.status,
      requestPermission,
      setSubscriptionList,
      topic,
    ],
  );

  const { checked, getInputProps } = useSwitch({
    checked: subscriptionList?.includes(topic) ?? false,
    disabled: !isEnabled,
    onChange: async event => {
      setIsLoading(true);
      await handleSwitchChange(event.target.checked);
      setIsLoading(false);
    },
  });

  if (!liveArticle?.pushNotifications) return null;

  return (
    <Container $isDisabled={!isEnabled} $isLoading={isLoading} className={className}>
      <Text>Enable alerts for all live updates</Text>
      <StyledLoading variant={LoadingVariant.Spinner} />
      <SwitchRoot $isChecked={checked}>
        <SwitchTrack />
        <SwitchThumb />
        <SwitchInput {...getInputProps()} />
      </SwitchRoot>
    </Container>
  );
};

LiveArticleLiveUpdateSwitch.displayName = "LiveArticleLiveUpdateSwitch";
